import React, { forwardRef, useId } from 'react';
import PropTypes from 'prop-types';
import { FIELD_SIZES } from '../../constants';
import { buildInputClasses, FieldMessage } from './fieldHelpers';

const Input = forwardRef(({
  type = 'text',
  value,
  onChange,
  placeholder,
  disabled = false,
  error = false,
  size = 'md',
  className = '',
  helperText,
  errorMessage,
  id: idProp,
  ...props
}, ref) => {
  const generatedId = useId();
  const inputId = idProp || generatedId;
  const messageId = `${inputId}-message`;
  const hasMessage = Boolean(helperText || errorMessage);
  const hasError = error || Boolean(errorMessage);

  const inputElement = (
    <input
      ref={ref}
      id={inputId}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={hasError || undefined}
      aria-describedby={hasMessage ? messageId : undefined}
      className={buildInputClasses({ size, error: hasError, className })}
      {...props}
    />
  );

  if (!hasMessage) return inputElement;

  return (
    <div className="w-full">
      {inputElement}
      <FieldMessage id={messageId} helperText={helperText} errorMessage={errorMessage} />
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  size: PropTypes.oneOf(FIELD_SIZES),
  className: PropTypes.string,
  helperText: PropTypes.string,
  errorMessage: PropTypes.string,
  id: PropTypes.string
};

export default Input;
